import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, InputLabel, Switch, Typography } from "@mui/material";
import theme from "css/theme/light";
import { FormFieldComponentEnum } from "pages/customer/pages/settings/pages/users/pages/edit/UserCustomField.interfaces";
import React from "react";
import Moment from "react-moment";
import { IFormFieldProps } from "../FormField/formfield.interface";
import FormLocationPicker from "./FormLocationPicker/FormLocationPicker";
import { IFormLocationPickerProps } from "./FormLocationPicker/formlocationpicker.interface";
import FormPreviewFileThumbnail from "./FormUploadFile/FormPreviewFileThumbnail";
import { IFormUploadAttachmentProps } from "./FormUploadFile/formuploadfile.interface";
import { useTranslation } from "react-i18next";
import TranslationData from "components/Form/FormTemplate/formtemplate.interface";
import FormNonPreviewFile from "components/Form/FormNonPreviewFile/FormNonPreviewFile";
import moment from "moment";

function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export interface IFormComponentView {
  field: IFormFieldProps;
  translationData?: TranslationData;
}

const FormComponentsView: React.FC<IFormComponentView> = ({
  field,
  translationData,
}) => {
  const { labelKey, labelDefault, value, component, options } = field;
  const { t } = useTranslation();
  const prefix = translationData?.formTypeKey ? translationData?.formTypeKey + "." : "";
  const label = t(prefix + labelKey, labelDefault || "");

  switch (component) {
    case FormFieldComponentEnum.CardButton: {
      const selected = options?.find((x) => x.id.toString() === value);
      return (
        <>
          <Typography variant="labelLarge" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{selected?.labelDefault}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.RadioButtons: {
      const find = options?.find((x) => x.value.toString() === value);
      const val = t(prefix + find?.labelKey, find?.labelDefault || "");
      return (
        <>
          <Typography variant="labelLarge" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{val}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.BigRadioButtons: {
      const find = options?.find((x) => x.value.toString() === value);
      const val = t(prefix + find?.labelKey, find?.labelDefault || "");
      return (
        <>
          <Typography variant="labelLarge" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{val}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.Checkbox: {
      const checked = value === "true";
      return (
        <>
          <FontAwesomeIcon
            icon={checked ? (faCheck as IconProp) : (faTimes as IconProp)}
            color={checked ? (theme.palette as any).primary["700"] : theme.palette.error["main"]}
          />
          <Typography variant="labelLarge" fontWeight={500} marginLeft={1}>
            {label}
          </Typography>
        </>
      );
    }

    case FormFieldComponentEnum.Switch: {
      const checked = value === "true";
      return (
        <>
          <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Switch checked={checked} disabled />
        </>
      );
    }

    case FormFieldComponentEnum.LocationPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      const componentProps: IFormLocationPickerProps = {
        labelKey,
        labelDefault,
        latLng: convertedValue?.value,
        value: "",
        disableSelect: true,
      };
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.interpretedValue}</Typography>
          <FormLocationPicker {...componentProps} mode="view" />
        </>
      );
    }

    case FormFieldComponentEnum.DateTimePicker: {
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">
            {value ? <Moment format="YYYY-MM-DD HH:mm">{value}</Moment> : <Box>-</Box>}
          </Typography>
        </>
      );
    }

    case FormFieldComponentEnum.DatePicker: {
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">
            {value ? <Moment format="YYYY-MM-DD">{value}</Moment> : <Box>-</Box>}
          </Typography>
        </>
      );
    }

    case FormFieldComponentEnum.VehiclePicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.RegNumber}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.Select: {
      const find = options?.find((x) => x.value.toString() === value);
      const val = t(prefix + find?.labelKey, find?.labelDefault || "");
      return (
        <>
          <Typography variant="labelLarge" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{val}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.BPLStationPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      return (
        <>
          <Typography variant="labelLarge" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.Name || ""}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.DepartmentPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.Name}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.CompanyPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.Name}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.UserPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null;
      return (
        <>
          <Typography variant="inputLabel" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{convertedValue?.Name}</Typography>
        </>
      );
    }

    case FormFieldComponentEnum.Upload: {
      const files = typeof field.value === "string" ? JSON.parse(value as string) : value;
      return (
        <Grid md={12} xs={12}>
          <InputLabel>
            <Typography variant="inputLabel">{label}</Typography>
          </InputLabel>
          {value !== "" && value !== null ? (
            <Grid container direction="row" spacing={2}>
              {files
                ?.filter((x: IFormUploadAttachmentProps) => x?.Data !== null && x?.Data !== undefined)
                .map((file: IFormUploadAttachmentProps) => {
                  const isPdfOrExcel =
                    file.fileName && /\.(pdf|xls|xlsx)$/i.test(file.fileName);
                  if (isPdfOrExcel) {
                    return (
                      <Grid item xs={12} key={file.fileId}>
                        <FormNonPreviewFile file={file} />
                      </Grid>
                    );
                  } else {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={file.fileId}>
                        <FormPreviewFileThumbnail {...file} />
                      </Grid>
                    );
                  }
                })}
            </Grid>
          ) : (
            <Typography variant="bodyMedium">{t("global.no_file", "No file")}</Typography>
          )}
        </Grid>
      );
    }

    default: {
      return (
        <>
          <Typography variant="inputLabel" fontWeight={500} color="textSecondary">
            {label}
          </Typography>
          <Typography variant="bodyMedium">{value !== null ? value : "-"}</Typography>
        </>
      );
    }
  }
};

export default FormComponentsView;
